import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Calendar, ArrowUpRight, Truck, RefreshCcw } from "lucide-react";
import { formatRateChip } from "@/lib/rentalPricing";

const COUNT_TONES = (n) =>
    n === 0 ? "bg-emerald-50 text-emerald-800 ring-emerald-200" :
    n <= 4 ? "bg-amber-50 text-amber-900 ring-amber-200" :
    "bg-red-50 text-red-800 ring-red-200";

export function RenewalRadarCard() {
    const [items, setItems] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [busyId, setBusyId] = useState(null);
    const [accRate, setAccRate] = useState(null);
    const [accSent, setAccSent] = useState(null);

    const load = async () => {
        try {
            const [{ data }, analytics] = await Promise.all([
                api.get("/admin/rentals/renewal-radar?within_days=30&limit=5"),
                api.get("/admin/renewals/analytics?within_days=90").catch(() => null),
            ]);
            setItems(data.items || []);
            setTotal(data.total || 0);
            if (analytics?.data) {
                setAccRate(analytics.data.acceptance_rate ?? 0);
                setAccSent(analytics.data.totals?.sent ?? 0);
            }
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setLoading(false); }
    };
    useEffect(() => { load(); }, []);

    const sendRenewalInvite = async (rental) => {
        if (busyId) return;
        setBusyId(rental.id);
        try {
            const { data: contract } = await api.post(`/admin/customers/${rental.customer_id}/contracts`, {
                type: "rental_agreement",
                title: `Renewal — ${rental.equipment_type || "rental"}${rental.unit_number ? ` · ${rental.unit_number}` : ""}`,
                is_renewal: true,
            });
            // Pre-populate the new draft's rental terms from the prior rental
            const payload = {
                equipment_type: rental.equipment_type,
                unit_number: rental.unit_number,
                make: rental.make || null,
                model: rental.model || null,
                year: rental.year || null,
                start_date: rental.renewal_date,
                daily_rate: rental.daily_rate ?? undefined,
                monthly_rate: rental.monthly_rate ?? undefined,
                billing_period: rental.billing_period || undefined,
                minimum_term_months: rental.minimum_term_months || undefined,
            };
            Object.keys(payload).forEach((k) => { if (payload[k] === undefined || payload[k] === null) delete payload[k]; });
            await api.post(`/admin/contracts/${contract.id}/rental`, payload);
            // Phase 7: auto-send for signature so the customer gets a friendly renewal email
            try {
                const { data: sent } = await api.post(`/admin/contracts/${contract.id}/send-for-signature`);
                toast.success(`Renewal invite sent · contract ${sent.title}`);
            } catch (sendErr) {
                const detail = sendErr?.response?.data?.detail || "";
                if (/email/i.test(detail)) {
                    toast.warning("Draft created — add an email and resend");
                } else {
                    toast.error(formatApiError(sendErr));
                }
            }
            window.location.href = `/admin/contracts/${contract.id}`;
        } catch (err) { toast.error(formatApiError(err)); setBusyId(null); }
    };

    return (
        <Card data-testid="admin-renewal-radar-card" className="h-full">
            <CardContent className="p-5 space-y-4">
                <div className="flex items-start justify-between gap-2">
                    <div>
                        <p className="text-[11px] font-medium tracking-[0.18em] uppercase text-slate-500 flex items-center gap-1.5">
                            <Calendar size={12} /> Renewal Radar
                        </p>
                        <h3 className="font-display text-lg font-semibold text-slate-900 mt-0.5">Next 30 days</h3>
                    </div>
                    <span className={`inline-flex items-center justify-center min-w-[2.25rem] h-9 px-2 rounded-full text-base font-bold ring-1 ring-inset ${COUNT_TONES(total)}`}
                        data-testid="renewal-radar-count">
                        {loading ? "…" : total}
                    </span>
                </div>

                {!loading && items.length === 0 && (
                    <div className="rounded-md border border-dashed border-slate-200 bg-slate-50/60 p-4 text-center"
                        data-testid="renewal-radar-empty">
                        <p className="text-sm text-slate-700 font-medium">No renewals due in the next 30 days.</p>
                        <p className="text-xs text-slate-500 mt-1">Min-term rentals appear here as their commitment ends.</p>
                    </div>
                )}

                {!loading && items.length > 0 && (
                    <ul className="divide-y divide-slate-100" data-testid="renewal-radar-list">
                        {items.map((r) => (
                            <li key={r.id} className="py-2.5 flex items-center justify-between gap-3"
                                data-testid={`renewal-radar-row-${r.id}`}>
                                <div className="min-w-0 flex-1">
                                    <Link to={`/admin/rentals/${r.id}`} className="text-sm font-medium text-slate-900 hover:text-blue-900 truncate block">
                                        {r.customer_company_name || "—"}
                                    </Link>
                                    <p className="text-[11px] text-slate-500 capitalize truncate">
                                        {(r.equipment_type || "—").replace("_", " ")}
                                        {r.unit_number ? ` · #${r.unit_number}` : ""}
                                        {formatRateChip(r) ? ` · ${formatRateChip(r)}` : ""}
                                    </p>
                                </div>
                                <div className="text-right shrink-0">
                                    <p className="text-xs font-semibold text-slate-900" data-testid={`renewal-radar-days-${r.id}`}>
                                        in {r.days_until_renewal} {r.days_until_renewal === 1 ? "day" : "days"}
                                    </p>
                                    <Button size="sm" variant="outline" className="h-7 px-2 text-[11px] gap-1 mt-1"
                                        disabled={busyId === r.id}
                                        onClick={() => sendRenewalInvite(r)}
                                        data-testid={`renewal-radar-invite-${r.id}`}>
                                        <RefreshCcw size={11} /> {busyId === r.id ? "…" : "Send renewal invite"}
                                    </Button>
                                </div>
                            </li>
                        ))}
                    </ul>
                )}

                <div className="pt-2 border-t border-slate-100 flex items-center justify-between gap-2">
                    <Link to="/admin/rentals/renewals" className="text-xs font-medium text-blue-900 hover:underline inline-flex items-center gap-1"
                        data-testid="renewal-radar-view-all">
                        View all renewals <ArrowUpRight size={11} />
                    </Link>
                    {accRate !== null && (
                        <Link to="/admin/renewals/analytics" className="text-[11px] text-slate-600 hover:text-blue-900 inline-flex items-center gap-1"
                            data-testid="renewal-radar-acceptance-footer">
                            <span className="font-semibold text-slate-900">{accRate.toFixed(1)}%</span>
                            <span>acceptance · 90d ({accSent} sent)</span>
                        </Link>
                    )}
                </div>
            </CardContent>
        </Card>
    );
}
